/**
 * BedrockSkillEffect — Earth tree shared trunk passive (earth_shared_passive_1).
 *
 * While the player has been stationary for at least 1000ms, incoming damage
 * is reduced by 15%. The bonus drops immediately on movement.
 *
 * This SkillEffect has no activation — it is always "on" once unlocked.
 * Its DR contribution is queried by `CombatHitResolver` via
 * `getActiveDamageReduction()` at damage application time, not pre-cached.
 *
 * UpgradeEffectResolver registration:
 * - key: 'damageReduction', scope: tree 'earth', value: 0.15, aggregation: sum
 * - stationaryRequirementMs: 1000 (passed to StationaryTracker on each query)
 *
 * Stacks additively with other tree-scoped DR passives (Warded, Iron Threshold,
 * Deep Root) — all sum before application in CombatHitResolver.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Nodes Array — Bedrock
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BedrockDeps {
  readonly stationary: {
    /**
     * Returns true when the player has been continuously stationary for at
     * least `thresholdMs` milliseconds. The tracker owns the state — Bedrock
     * only queries it.
     */
    isStationary(thresholdMs: number): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BedrockConfig {
  /** Damage reduction fraction applied while stationary — base 15%. */
  readonly damageReduction: number;
  /** Minimum stationary duration (ms) before DR activates. */
  readonly stationaryRequirementMs: number;
}

const DEFAULT_CONFIG: BedrockConfig = {
  damageReduction: 0.15,        // 15% incoming DR
  stationaryRequirementMs: 1000, // 1000ms stationary threshold
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BedrockSkillEffect implements SkillEffect {
  readonly skillId = 'earth_shared_passive_1';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BedrockDeps;
  private readonly config: BedrockConfig;

  constructor(deps: BedrockDeps, config: Partial<BedrockConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Passive query interface ---

  /**
   * Returns the active damage reduction fraction at the moment of the call.
   * CombatHitResolver calls this during damage calculation — not pre-cached.
   *
   * Returns `config.damageReduction` (0.15) when the stationary condition is
   * met; returns 0 otherwise.
   */
  getActiveDamageReduction(): number {
    if (!this.deps.stationary.isStationary(this.config.stationaryRequirementMs)) {
      return 0;
    }
    const resolved = this.deps.resolver.getAdditiveEffect('earth_shared_passive_1', 'damageReduction');
    return resolved !== 0 ? resolved : this.config.damageReduction;
  }

  /** Whether the DR bonus is currently active (stationary condition met). */
  get isBedrockActive(): boolean {
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  get damageReduction(): number { return this.config.damageReduction; }
  get stationaryRequirementMs(): number { return this.config.stationaryRequirementMs; }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — stationary state is owned by StationaryTracker, not this effect
  }

  destroy(): void {
    // No owned resources
  }
}
